<template>
    <div class="bio-panel-item__list-item clearfix">
        <div class="bio-panel-item__code-container">
            <div class="bio-panel-item__seq-number">
                <span
                    class="bio-panel-item__code"
                    :data-id="codeId"
                    :data-code="seqData.sequence_hash_id"
                    :data-seq-number="seqData.sequence_number"
                    :data-title="sequenceName"
                >Seq Code {{ seqData.sequence_number }}</span>
                <span slot="reference" class="bio-panel-item__reference" :title="getIdentityVal(seqData)">
                    {{ getIdentityVal(seqData) }}
                </span>
            </div>
            <ElPopover
                popperClass="bio-panel__popover"
                :visibleArrow="false"
                placement="top"
                trigger="hover"
                :disabled="!operatorDisabled"
                :content="operatorTitle"
            >
                <div slot="reference" class="bio-panel-item__seq-operator" :class="{ disabled: operatorDisabled }">
                    <div
                        class="icon"
                        @click.prevent.stop="goToSelectedSeq(seqData.highlight_key, 'prev', seqData.sequence_id_number)"
                    >
                        <svg class="snap-zen-icon" aria-hidden="true">
                            <use xlink:href="#zen-icon-caret-up" />
                        </svg>
                    </div>
                    <div
                        class="icon"
                        @click.prevent.stop="goToSelectedSeq(seqData.highlight_key, 'next', seqData.sequence_id_number)"
                    >
                        <svg class="snap-zen-icon" aria-hidden="true">
                            <use xlink:href="#zen-icon-caret-down" />
                        </svg>
                    </div>
                </div>
            </ElPopover>
        </div>
        <div v-if="seqIdLabel" class="bio-panel-item__description" :title="seqIdLabel">
            {{ seqIdLabel }}
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { Popover } from 'element-ui';
import { mapGetters } from 'vuex';
import { transformSequencePosition } from '@patsnap-utils/sequence';

export default {
    components: {
        ElPopover: Popover,
    },
    props: {
        seqData: {
            require: true,
            default: '',
        },
        highlightType: {
            type: String,
        },
    },
    computed: {
        ...mapGetters({
            bioHighlightEnable: 'bio/highlightEnable',
        }),
        hasNoSeqId() {
            return !_.get(this.seqData, 'sequence_id_number', []).length;
        },
        highlightKey() {
            return _.get(this.seqData, 'highlight_key', []);
        },
        isNotCurrentPage() {
            const highLightKey = _.get(this.seqData, 'highlight_key', []);
            const path = {
                '/desc': 'description',
                '/clms': 'claim',
                '/clms\'figures': 'claim',
                '/desc\'figures': 'description',
            };
            let routePath = this.$route.path || '';
            routePath = routePath.replace(/\/$/, '');
            return !highLightKey.some(({ page_tab }) => page_tab.indexOf(path[routePath]) !== -1);
        },
        operatorDisabled() {
            const highLightKey = _.get(this.seqData, 'highlight_key', []);
            return this.hasNoSeqId || !highLightKey.length || this.isNotCurrentPage;
        },
        operatorTitle() {
            if (this.hasNoSeqId) {
                return this.$t('bio.assistant.noreq');
            }
            if (!_.get(this.seqData, 'highlight_key', []).length) {
                return this.$t('bio.assistant.nointext');
            }
            if (this.isNotCurrentPage) {
                const keys = _.get(this.seqData, 'highlight_key', []);
                const isClaim = keys.some(({ page_tab }) => page_tab.indexOf('claim') !== -1);

                return this.$t('bio.assistant.otherpage', { page: isClaim ? this.$t('CLMS') : this.$t('DESC') });
            }
            return '';
        },
        codeId() {
            return this.seqData.sequence_id_number ? this.seqData.sequence_id_number[0] : '';
        },
        sequenceName() {
            return `Seq Code ${this.seqData.sequence_number}${this.getSequenceNoList(this.seqData)}`;
        },
        seqIdLabel() {
            const transMap = {
                'description': 'Desc',
                'claim': 'Claim',
            };
            const validTab = ['description', 'claim'];
            const pageTabs = (this.seqData.highlight_key || []).map(o => o.page_tab)
                .flat()
                .filter(tab => validTab.includes(tab));
            const transTabs = Array.from(new Set(pageTabs)).map(tab => transMap[tab])
                .join(',');
            const seqIds = this.sequencePosition(this.seqData)
                .join('; ') || '';
            return transTabs ? `${transTabs}: ${seqIds}` : seqIds;
        },
    },
    methods: {
        goToSelectedSeq(keys, scrollType, seqIdNums) {
            if (this.operatorDisabled) {
                return;
            }
            //
            this.$emit('update-seqid', {
                highlightType: this.highlightType,
                keys,
                next: scrollType === 'next',
                seqIdNums,
            });
        },
        getIdentityVal(item) {
            return item.identity_value ? `(${_.round(item.identity_value * 100, 2)}%)` : '';
        },
        getSequenceNoList(item) {
            const arrs = this.sequencePosition(item);
            return `(${(item.identity_value ? [`${_.round(item.identity_value * 100, 2)}%`] : []).concat(arrs)
                .join('; ') || '-'})`;
        },
        sequencePosition(item) {
            const position = _.get(item, 'location', {});
            const nos = _.get(item, 'sequence_id_number', []);
            return transformSequencePosition(position, nos, this.$t.bind(this), 'bio.position');
        },
    },
};
</script>
<style lang="scss">
.bio-panel-item {
    &__list-item {
        line-height: 20px;
        margin: 4px 20px 0 14px;
    }

    &__code-container {
        display: flex;
    }

    &__description {
        font-size: 12px;
        line-height: 16px;
        color: #495973;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    &__seq-number {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        margin-right: 15px;
        text-overflow: ellipsis;
    }
    &__seq-operator {
        border: 1px solid #d1d4db;
        border-radius: 4px;
        display: flex;

        .icon {
            float: left;
            width: 20px;
            height: 20px;
            line-height: 20px;
            text-align: center;
            cursor: pointer;
            color: #7a869a;
            display: flex;
            align-items: center;
            justify-content: center;
            user-select: none;
        }
        .icon:hover {
            background: $neutral-blue-30;
        }
        .icon:first-child {
            border-right: 1px solid #f0f0f0;
        }

        &.disabled {
            opacity: 0.5;

            .icon:hover {
                background: transparent;
            }
        }
    }
    &__code {
        color: #1976D2;
        &:hover {
            text-decoration: underline;
            cursor: pointer;
        }
    }
    &__reference {
        cursor: pointer;
    }
}
</style>
